/**
 * Workshop — interactive workshop lesson format.
 *
 * A lesson whose content is a JSON document with format: "workshop" is
 * rendered as an interactive slide workshop (LessonWorkshop) instead of
 * markdown. Any lesson that doesn't parse as a workshop doc renders the
 * normal way, so the format is fully backward compatible.
 *
 * LEGACY: content authored before the rename carries format: "airlearn".
 * Both values are accepted so stored lessons keep working; new content is
 * always written as "workshop".
 *
 * Document shape:
 * {
 *   "format": "workshop",
 *   "version": 1,
 *   "title": "Workshop title",
 *   "intro": "one-line description shown on the start screen",
 *   "steps": [
 *     { "type": "slide", "kicker": "...", "title": "...", "body": "markdown", "emoji": "💵" },
 *     { "type": "quiz", "question": "...", "options": ["..."], "answer": 0, "explain": "..." },
 *     { "type": "poll", "question": "...", "options": ["..."], "reveal": "markdown" },
 *     { "type": "scenario", "prompt": "...", "choices": [
 *         { "label": "...", "outcome": "markdown", "verdict": "good" | "risky" | "bad" }
 *     ]},
 *     { "type": "estimate", "question": "...", "min": 0, "max": 100, "step": 1,
 *       "unit": "%", "answer": 42, "tolerance": 10, "reveal": "markdown" },
 *     { "type": "reflect", "prompt": "...", "bioKey": "goal-12-months",
 *       "bioLabel": "My 12-month money goal", "placeholder": "...",
 *       "isGoal": true }
 *   ],
 *   "outro": { "title": "...", "body": "markdown recap" }
 * }
 *
 * "reflect" steps are short written answers saved to the student's profile
 * as their Money Bio (goals render in their own section when isGoal=true).
 */

const STEP_TYPES = ['slide', 'quiz', 'poll', 'scenario', 'estimate', 'reflect'];

// Current format value plus the pre-rename value still present in stored content
const FORMAT_VALUES = ['workshop', 'airlearn'];

/**
 * Try to parse lesson content as a workshop doc.
 * @param {string} content - raw lesson content
 * @returns {object|null} the parsed doc, or null if this isn't a valid Workshop lesson
 */
export function parseWorkshopDoc(content) {
  if (!content || typeof content !== 'string') return null;

  const trimmed = content.trim();
  // Cheap pre-check before attempting JSON.parse on ordinary lessons
  if (!trimmed.startsWith('{')) return null;
  if (!FORMAT_VALUES.some((f) => trimmed.includes(`"${f}"`))) return null;

  let doc;
  try {
    doc = JSON.parse(trimmed);
  } catch (_) {
    return null;
  }

  if (!doc || !FORMAT_VALUES.includes(doc.format) || !Array.isArray(doc.steps) || doc.steps.length === 0) {
    return null;
  }

  // Keep only steps the renderer understands, and sanity-check each type
  const steps = doc.steps.filter((step) => {
    if (!step || !STEP_TYPES.includes(step.type)) return false;
    switch (step.type) {
      case 'slide':
        return typeof step.body === 'string' || typeof step.title === 'string';
      case 'quiz':
        return typeof step.question === 'string'
          && Array.isArray(step.options) && step.options.length >= 2
          && Number.isInteger(step.answer) && step.answer >= 0 && step.answer < step.options.length;
      case 'poll':
        return typeof step.question === 'string'
          && Array.isArray(step.options) && step.options.length >= 2;
      case 'scenario':
        return typeof step.prompt === 'string'
          && Array.isArray(step.choices) && step.choices.length >= 2
          && step.choices.every((c) => c && typeof c.label === 'string' && typeof c.outcome === 'string');
      case 'estimate':
        return typeof step.question === 'string'
          && Number.isFinite(step.min) && Number.isFinite(step.max) && step.max > step.min
          && Number.isFinite(step.answer);
      case 'reflect':
        return typeof step.prompt === 'string' && typeof step.bioKey === 'string' && step.bioKey.length > 0;
      default:
        return false;
    }
  });

  if (steps.length === 0) return null;

  return { ...doc, steps };
}

/** True when a step type requires interaction before advancing. */
export function isInteractiveStep(step) {
  return step && step.type !== 'slide';
}

/** Count the scored checkpoints (quizzes + estimates) in a doc. */
export function countCheckpoints(doc) {
  return doc.steps.filter((s) => s.type === 'quiz' || s.type === 'estimate').length;
}
